import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRightLeft, Check, Loader2, Plus, ScanSearch, ShieldAlert } from "lucide-react";

const bc = { borderColor: "var(--ll-border)" };

export const useAccountCheck = (refreshKey) => {
  const [check, setCheck] = useState({ mismatches: {}, scan: null });
  const load = useCallback(() => api.get("/statements/account-check").then((r) => setCheck(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    if (check.scan?.status !== "running") return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [check.scan?.status, load]);
  return { ...check, reload: load };
};

export function AccountMismatchBadge({ m, statement, canFix, onChanged }) {
  const [busy, setBusy] = useState(null);
  if (!m) return null;
  const id = statement.id;
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); toast.success(ok); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(null); }
  };
  const move = (accId) => api.post(`/statements/${id}/move`, { bank_account_id: accId });
  const create = async () => {
    const r = await api.post("/bank-accounts", {
      name: `Account ••${m.detected}`, bank_name: m.bank_name || "Bank",
      account_number_masked: `•••• ${m.detected}`, account_type: m.account_type || "checking",
    });
    await move(r.data.id);
  };
  const src = m.source === "statement" ? "the account number printed on the statement" : "the file name";
  const btn = "text-[10px] px-1.5 py-0.5 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1 whitespace-nowrap transition-colors disabled:opacity-50";
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" data-testid={`statement-acct-mismatch-${id}`}>
      <span
        title={`Based on ${src}, this statement is for ••${m.detected}, but it's in ${m.account_name} (••${m.account_last4}).`}
        className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 inline-flex items-center gap-1 whitespace-nowrap"
        style={{ background: "#FEF2F2", color: "var(--ll-error)", border: "1px solid var(--ll-error)" }}
      >
        <ShieldAlert size={10} /> Wrong account? ••{m.detected}
      </span>
      {canFix && (m.match ? (
        <button className={btn} style={bc} disabled={!!busy} data-testid={`statement-acct-move-${id}`}
          onClick={() => run("move", () => move(m.match.id), `Moved to ${m.match.name}`)}>
          {busy === "move" ? <Loader2 size={10} className="animate-spin" /> : <ArrowRightLeft size={10} />} Move to {m.match.name}
        </button>
      ) : (
        <button className={btn} style={bc} disabled={!!busy} data-testid={`statement-acct-create-${id}`}
          onClick={() => run("create", create, `Created account ••${m.detected} and moved the statement`)}>
          {busy === "create" ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />} Create account ••{m.detected}
        </button>
      ))}
      {canFix && (
        <button className={btn} style={bc} disabled={!!busy} title="The account is correct — hide this warning" data-testid={`statement-acct-dismiss-${id}`}
          onClick={() => run("dismiss", () => api.post(`/statements/${id}/account-check/dismiss`), "Marked as correct")}>
          <Check size={10} /> It's correct
        </button>
      )}
    </span>
  );
}

export function AccountCheckBanner({ check, canFix, onScanned }) {
  const count = Object.keys(check.mismatches).length;
  const scan = check.scan;
  const running = scan?.status === "running";
  const start = async () => {
    try { await api.post("/statements/account-scan"); toast.success("Reading account numbers from your statements…"); onScanned(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't start the scan"); }
  };
  if (!count && !canFix) return null;
  return (
    <div className="mb-5 p-4 border flex flex-wrap items-center justify-between gap-3"
      style={count ? { background: "#FEF2F2", borderColor: "var(--ll-error)" } : { background: "#fff", ...bc }} data-testid="account-check-banner">
      <div className="text-sm flex items-center gap-2">
        <ShieldAlert size={16} className={count ? "text-[var(--ll-error)]" : "text-[var(--ll-ink-muted)]"} />
        {count ? (
          <span data-testid="account-check-count"><b>{count} statement{count === 1 ? "" : "s"}</b> may be in the wrong bank account. That causes chain breaks. Use Move or Create on each flagged row.</span>
        ) : <span>Account numbers match on every checked statement.</span>}
      </div>
      {canFix && (
        <div className="flex items-center gap-3">
          {scan && (
            <span className="text-xs text-[var(--ll-ink-soft)] font-mono-fin" data-testid="account-scan-status">
              {running ? `Scanning ${scan.done}/${scan.total}…` : `Last AI scan: ${scan.found}/${scan.total} numbers read`}
            </span>
          )}
          <button onClick={start} disabled={running} data-testid="account-scan-btn"
            className="text-xs px-3 py-1.5 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors" style={bc}>
            {running ? <Loader2 size={12} className="animate-spin" /> : <ScanSearch size={12} />} Read account numbers with AI
          </button>
        </div>
      )}
    </div>
  );
}
